import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Polls for an integration whose connection finishes in another browser tab
 * (GitHub App install, GitLab OAuth, Slack install, cloud IAM consoles).
 *
 * Real installs take minutes: the user is reading GitHub's permission screen
 * and picking repositories while we wait. The previous implementation polled
 * for 30 seconds and then showed "Connection timeout" even though the
 * installation went on to succeed; a user hit exactly that. So this hook:
 *
 *   - polls for up to `timeoutMs` (default 10 minutes), not 30 seconds
 *   - re-checks immediately when the window regains focus or becomes visible
 *     (the user coming back from the provider's tab)
 *   - exposes `checkNow` for a manual "Check now" button
 *   - never turns elapsed time into an error; `onTimeout` lets the caller show
 *     a neutral "still waiting" message instead
 *   - cleans up its timer on unmount
 *
 * `check` returns true once the integration is connected.
 */
export interface ConnectionPollerOptions {
  /** Returns true when the integration is connected. Errors are swallowed (logged) and polling continues. */
  check: () => Promise<boolean>;
  onConnected?: () => void;
  /** Called once when timeoutMs elapses without a connection. Polling stops; the user can start again. */
  onTimeout?: () => void;
  intervalMs?: number;
  timeoutMs?: number;
}

export interface ConnectionPoller {
  /** True while polling. */
  waiting: boolean;
  start: () => void;
  stop: () => void;
  /** Run one check immediately (e.g. a "Check now" button). Returns the connection state. */
  checkNow: () => Promise<boolean>;
}

export const DEFAULT_CONNECTION_POLL_INTERVAL_MS = 3000;
export const DEFAULT_CONNECTION_POLL_TIMEOUT_MS = 10 * 60 * 1000;

export function useConnectionPoller(options: ConnectionPollerOptions): ConnectionPoller {
  const { intervalMs = DEFAULT_CONNECTION_POLL_INTERVAL_MS, timeoutMs = DEFAULT_CONNECTION_POLL_TIMEOUT_MS } = options;
  const [waiting, setWaiting] = useState(false);

  // Latest callbacks without re-subscribing listeners on every render.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const deadlineRef = useRef<number>(0);
  const inFlightRef = useRef(false);
  const activeRef = useRef(false);

  const stop = useCallback(() => {
    activeRef.current = false;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setWaiting(false);
  }, []);

  const runCheck = useCallback(async (): Promise<boolean> => {
    if (inFlightRef.current) return false;
    inFlightRef.current = true;
    try {
      const connected = await optionsRef.current.check();
      if (connected && activeRef.current) {
        stop();
        optionsRef.current.onConnected?.();
      }
      return connected;
    } catch (err) {
      // A transient status failure must not end the wait; the next tick retries.
      console.error('Connection poll failed:', err);
      return false;
    } finally {
      inFlightRef.current = false;
    }
  }, [stop]);

  const start = useCallback(() => {
    stop();
    activeRef.current = true;
    deadlineRef.current = Date.now() + timeoutMs;
    setWaiting(true);
    timerRef.current = setInterval(async () => {
      if (!activeRef.current) return;
      if (Date.now() >= deadlineRef.current) {
        stop();
        optionsRef.current.onTimeout?.();
        return;
      }
      await runCheck();
    }, intervalMs);
  }, [intervalMs, timeoutMs, runCheck, stop]);

  const checkNow = useCallback(() => runCheck(), [runCheck]);

  // The user comes back from the provider's tab: check right away rather than
  // waiting for the next tick.
  useEffect(() => {
    const onReturn = () => {
      if (activeRef.current && document.visibilityState !== 'hidden') void runCheck();
    };
    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);
    return () => {
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onReturn);
    };
  }, [runCheck]);

  // Never leave a timer running after the page is gone.
  useEffect(() => stop, [stop]);

  return { waiting, start, stop, checkNow };
}
